import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "../scripts/AuthContext";

import { API_BASE_URL } from "@/lib/apiBase";
const EditMemberTitleDialog = ({ isOpen, onClose, member, orgId, onSuccess }) => {
  const { accessToken, login, logout } = useAuth();
  const [title, setTitle] = useState("");
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize title when member changes
  useEffect(() => {
    if (member) {
      setTitle(member.title || "");
      setError(null);
    }
  }, [member]);

  // Helper function to make authenticated API calls with token refresh
  const makeAuthenticatedRequest = async (requestFn, token = accessToken) => {
    try {
      return await requestFn(token);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        if (err.response.data.message === "Token has expired") {
          try {
            const refreshResponse = await axios.post(
              `${API_BASE_URL}/auth/refresh`,
              null,
              {
                withCredentials: true,
              }
            );
            const newAccessToken = refreshResponse.data.access_token || refreshResponse.data.accessToken;
            login(newAccessToken);
            return await requestFn(newAccessToken);
          } catch (refreshError) {
            console.error("Failed to refresh access token:", refreshError);
            logout();
            throw new Error("Session expired. Please log in again.");
          }
        }
      }
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!member || !orgId) {
      setError("Missing member or organization information");
      return;
    }

    setIsUpdating(true);

    try {
      await makeAuthenticatedRequest(async (token) => {
        const response = await axios.patch(
          `${API_BASE_URL}/org/${orgId}/members/${member.user.user_id}/title`,
          { title: title.trim() || null },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return response.data;
      });

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Close dialog
      onClose();
    } catch (err) {
      console.error("Failed to update title:", err);
      setError(err.response?.data?.message || err.message || "Failed to update title");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    setTitle("");
    setError(null);
    onClose();
  };

  if (!member) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Title</DialogTitle>
          <DialogDescription>
            Update your title in this organization
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="member-name">Member</Label>
              <Input
                id="member-name"
                value={`${member.user.first_name} ${member.user.last_name}`}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., CEO, Software Engineer, Product Manager"
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to remove your title
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? "Updating..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditMemberTitleDialog;

